import { useState, useEffect } from 'react'
import { Gavel, Plus, Edit, Trash2, Eye, Calendar, MapPin, Play, Pause, Search, Filter, ChevronLeft, ChevronRight, Menu, Globe, Pin, PinOff, BanIcon } from 'lucide-react'
import { useToast } from '../../hooks/useToast'
import Toast from '../../components/ui/Toast'
import ConfirmDialog from '../../components/ui/ConfirmDialog'
import { useOrganizador } from '../../contexts/OrganizadorContext'
import { useSidebar } from '../../components/layout/Layout'
import { usePermissao } from '../../hooks/usePermissao'
import leiloesService from '../../services/leiloesService'
import organizadoresService from '../../services/organizadoresService'
import { lotesService } from '../../services/lotesService'

export default function Leiloes() {
  const { toasts, success, error, hideToast } = useToast()
  const { organizadorAtivo, loadOrganizadorCompleto } = useOrganizador()
  const { setSidebarOpen } = useSidebar()
  const { podeEditar } = usePermissao()
  const canEdit = podeEditar('leiloes')
  const [leiloes, setLeiloes] = useState([])
  const [todosLeiloes, setTodosLeiloes] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [filtros, setFiltros] = useState({
    tipo_leilao: '',
    status: '',
    data_inicio: '',
    data_fim: ''
  })
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalItems, setTotalItems] = useState(0)
  const [confirmDelete, setConfirmDelete] = useState(null)
  const [confirmBloqueioSync, setConfirmBloqueioSync] = useState(null)
  const [pinLoading, setPinLoading] = useState(false)
  const itemsPerPage = 12

  const organizadorId = organizadorAtivo?.id
  const temGestaoFinanceira = organizadorAtivo?.produtos_premium?.includes('gestao_financeira')

  // ID do leilão que aparece no catálogo:
  // - se há pin manual (catalogo_leilao_fixado) e esse leilão está ativo → é o pinado
  // - senão → o ativo mais recente pela data (calculado a partir da lista carregada)
  const leilaoFixadoId = organizadorAtivo?.catalogo_leilao_fixado ?? null

  useEffect(() => {
    if (organizadorId) {
      loadLeiloes()
    }
  }, [organizadorId, filtros, searchTerm, currentPage])

  const loadLeiloes = async () => {
    if (!organizadorId) return
    
    try {
      setLoading(true)
      const params = {
        ...filtros,
        todos: true,
        search: searchTerm,
        page: currentPage,
        limit: itemsPerPage
      }
      
      const data = await leiloesService.getByOrganizador(organizadorId, params)

      // Simular paginação no frontend por enquanto
      let filteredData = data || []
      setTodosLeiloes(filteredData)
      
      // Filtrar por busca
      if (searchTerm) {
        filteredData = filteredData.filter(leilao => 
          leilao.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
          leilao.cidade?.toLowerCase().includes(searchTerm.toLowerCase())
        )
      }
      
      // Filtrar por período
      if (filtros.data_inicio) {
        filteredData = filteredData.filter(leilao => 
          new Date(leilao.data_leilao) >= new Date(filtros.data_inicio)
        )
      }
      
      if (filtros.data_fim) {
        filteredData = filteredData.filter(leilao => 
          new Date(leilao.data_leilao) <= new Date(filtros.data_fim)
        )
      }
      
      // Paginação
      const total = filteredData.length
      const totalPgs = Math.ceil(total / itemsPerPage)
      const startIndex = (currentPage - 1) * itemsPerPage
      const paginatedData = filteredData.slice(startIndex, startIndex + itemsPerPage)
      
      setLeiloes(paginatedData)
      setTotalPages(totalPgs)
      setTotalItems(total)
    } catch (err) {
      console.error('Erro ao carregar leilões:', err)
      error('Erro ao carregar leilões')
      setLeiloes([])
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (leilao) => {
    setConfirmDelete({ leilao, lotesCount: null, loadingDetails: true })
    try {
      const lotes = await lotesService.listarLotesPorLeilao(organizadorId, leilao.id)
      setConfirmDelete({ leilao, lotesCount: lotes.length, loadingDetails: false })
    } catch {
      setConfirmDelete({ leilao, lotesCount: 0, loadingDetails: false })
    }
  }

  const confirmDeleteAction = async () => {
    if (!confirmDelete) return
    try {
      await leiloesService.delete(organizadorId, confirmDelete.leilao.id)
      success('Leilão excluído com sucesso!')
      await loadLeiloes()
    } catch (err) {
      console.error('Erro ao excluir leilão:', err)
      const errorMessage = err.response?.data?.detail || 'Erro ao excluir leilão'
      error(errorMessage)
    }
    setConfirmDelete(null)
  }

  const toggleStatus = async (leilao) => {
    try {
      const novoStatus = leilao.status === 'on' ? 'off' : 'on'
      await leiloesService.update(organizadorId, leilao.id, { status: novoStatus })
      success(`Leilão ${novoStatus === 'on' ? 'ativado' : 'desativado'} com sucesso!`)
      await loadLeiloes()
    } catch (err) {
      console.error('Erro ao alterar status:', err)
      error('Erro ao alterar status do leilão')
    }
  }

  const toggleSyncGestao = (leilao) => {
    if (!leilao.bloquear_sync_gestao) {
      setConfirmBloqueioSync(leilao)
    } else {
      executarToggleSync(leilao, false)
    }
  }

  const executarToggleSync = async (leilao, novoValor) => {
    try {
      await leiloesService.update(organizadorId, leilao.id, { bloquear_sync_gestao: novoValor })
      success(novoValor ? 'Sincronização bloqueada e lançamentos removidos' : 'Sincronização com Gestão Financeira liberada')
      await loadLeiloes()
    } catch (err) {
      console.error('Erro ao alterar sync gestão:', err)
      error('Erro ao alterar configuração de sincronização')
    } finally {
      setConfirmBloqueioSync(null)
    }
  }

  // Qual leilão aparece no catálogo agora:
  // - pin ativo e leilão ativo → o pinado
  // - senão → o ativo com data mais recente
  const leilaoNoCatalogoId = (() => {
    const ativos = todosLeiloes.filter(l => l.status === 'on')
    if (!ativos.length) return null
    if (leilaoFixadoId) {
      const pinado = ativos.find(l => l.id === leilaoFixadoId)
      if (pinado) return pinado.id
    }
    const sorted = [...ativos].sort((a, b) => {
      if (!a.data_leilao && !b.data_leilao) return b.id - a.id
      if (!a.data_leilao) return 1
      if (!b.data_leilao) return -1
      return new Date(b.data_leilao) - new Date(a.data_leilao) || b.id - a.id
    })
    return sorted[0]?.id ?? null
  })()

  const handlePin = async (leilao) => {
    if (!canEdit) return
    setPinLoading(true)
    try {
      const isAtual = leilao.id === leilaoNoCatalogoId
      const isPinado = leilao.id === leilaoFixadoId

      // Se já é o pinado manualmente → remove o pin (volta para automático)
      // Se é o automático sem pin → aplica o pin nele
      // Se é outro leilão → aplica o pin nele
      const novoPin = isPinado ? null : leilao.id

      await organizadoresService.setCatalogoLeilaoFixado(organizadorId, novoPin)
      await loadOrganizadorCompleto(organizadorId)
      success(novoPin ? 'Leilão fixado no catálogo!' : 'Pin removido — catálogo voltou ao automático')
    } catch (err) {
      console.error('Erro ao alterar pin do catálogo:', err)
      error('Erro ao alterar leilão do catálogo')
    } finally {
      setPinLoading(false)
    }
  }

  const formatDate = (dateString, timeString) => {
    if (!dateString) return '-'
    
    const date = new Date(dateString)
    let formattedDate = date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
    
    if (timeString) {
      formattedDate += ` às ${timeString}`
    }
    
    return formattedDate
  }

  const getTipoLabel = (tipo) => {
    const tipos = {
      'gado_corte': 'Gado de Corte',
      'gado_leite': 'Gado de Leite',
      'gado_elite': 'Gado de Elite',
      'cavalo': 'Cavalo',
      'beneficente': 'Beneficente'
    }
    return tipos[tipo] || tipo
  }

  const getTipoColor = (tipo) => {
    const cores = {
      'gado_corte': 'bg-blue-100 text-blue-800',
      'gado_leite': 'bg-green-100 text-green-800',
      'gado_elite': 'bg-purple-100 text-purple-800',
      'cavalo': 'bg-amber-100 text-amber-800',
      'beneficente': 'bg-pink-100 text-pink-800'
    }
    return cores[tipo] || 'bg-gray-100 text-gray-800'
  }

  const renderCatalogo = (leilao) => {
    const noCatalogo = leilao.id === leilaoNoCatalogoId
    const isPinado = leilao.id === leilaoFixadoId
    const isAtivo = leilao.status === 'on'

    if (!noCatalogo && !isAtivo) return <span className="text-gray-300">—</span>

    return (
      <div className="flex items-center gap-2">
        {noCatalogo && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
            <Globe className="w-3 h-3" />
            {isPinado ? 'Fixado' : 'Auto'}
          </span>
        )}
        {canEdit && isAtivo && (
          <button
            onClick={() => handlePin(leilao)}
            disabled={pinLoading}
            title={isPinado ? 'Remover pin (voltar ao automático)' : 'Fixar este leilão no catálogo'}
            className={`p-1 rounded transition-colors ${
              isPinado
                ? 'text-emerald-600 hover:bg-emerald-50'
                : 'text-gray-400 hover:text-emerald-600 hover:bg-emerald-50'
            }`}
          >
            {isPinado ? <PinOff className="w-4 h-4" /> : <Pin className="w-4 h-4" />}
          </button>
        )}
      </div>
    )
  }

  const renderAcoes = (leilao) => (
    <div className="flex items-center justify-end space-x-2">
      <button
        onClick={() => window.location.href = `/leiloes/${leilao.id}`}
        className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50 transition-colors"
        title="Visualizar"
      >
        <Eye className="w-4 h-4" />
      </button>
      {canEdit && (
        <>
          {temGestaoFinanceira && (
            <button
              onClick={() => toggleSyncGestao(leilao)}
              className={`p-1 rounded transition-colors ${
                leilao.bloquear_sync_gestao
                  ? 'text-yellow-600 hover:bg-yellow-50'
                  : 'text-gray-400 hover:text-yellow-600 hover:bg-yellow-50'
              }`}
              title={leilao.bloquear_sync_gestao ? 'Liberar sync com Gestão Financeira' : 'Bloquear sync com Gestão Financeira'}
            >
              <BanIcon className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={() => window.location.href = `/leiloes/${leilao.id}/editar`}
            className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50 transition-colors"
            title="Editar"
          >
            <Edit className="w-4 h-4" />
          </button>
          <button
            onClick={() => handleDelete(leilao)}
            className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50 transition-colors"
            title="Excluir"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </>
      )}
    </div>
  )

  if (loading && leiloes.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-rematei-blue"></div>
      </div>
    )
  }

  return (
    <>
      {/* Toasts */}
      {toasts.map(toast => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          duration={toast.duration}
          onClose={() => hideToast(toast.id)}
        />
      ))}

      {/* Modal de Confirmação */}
      {(() => {
        const leilao = confirmDelete?.leilao
        const lotesCount = confirmDelete?.lotesCount ?? null
        const loadingDetails = confirmDelete?.loadingDetails ?? false
        const temLotes = lotesCount !== null && lotesCount > 0
        const local = [leilao?.cidade, leilao?.uf].filter(Boolean).join(', ') || leilao?.local || '-'
        const details = loadingDetails ? [] : [
          { label: 'Leilão', value: leilao?.nome || '-' },
          { label: 'Data', value: leilao?.data_leilao ? formatDate(leilao.data_leilao, leilao.hora_inicio) : '-' },
          { label: 'Local', value: local },
          { label: 'Status', value: leilao?.status === 'on' ? 'Ativo' : 'Inativo' },
          { label: 'Lotes', value: lotesCount !== null ? `${lotesCount} lote(s)` : '-', danger: temLotes },
        ]
        return (
          <ConfirmDialog
            isOpen={!!confirmDelete}
            onClose={() => setConfirmDelete(null)}
            onConfirm={confirmDeleteAction}
            title={temLotes ? 'Não é possível excluir' : 'Excluir Leilão'}
            message={
              temLotes
                ? `O leilão "${leilao?.nome}" possui ${lotesCount} lote(s). Remova todos os lotes antes de excluir o leilão.`
                : `Tem certeza que deseja excluir este leilão? Esta ação não pode ser desfeita.`
            }
            details={details}
            blocked={temLotes}
            loading={loadingDetails}
            confirmText="Excluir"
            cancelText="Cancelar"
            type="danger"
          />
        )
      })()}

      {confirmBloqueioSync && (
        <ConfirmDialog
          isOpen={!!confirmBloqueioSync}
          onClose={() => setConfirmBloqueioSync(null)}
          onConfirm={() => executarToggleSync(confirmBloqueioSync, true)}
          title="Bloquear sincronização com Gestão Financeira"
          message={`Tem certeza que deseja bloquear a sincronização do leilão "${confirmBloqueioSync.nome}"?`}
          details={[
            { label: 'O que vai acontecer', value: 'Todos os lançamentos de comissão a receber e comprador × vendedor já gerados para este leilão serão EXCLUÍDOS permanentemente.' },
            { label: 'Futuras sincronizações', value: 'Este leilão não gerará novos lançamentos na Gestão Financeira.' },
            { label: 'Para reverter', value: 'Clique no ícone novamente para liberar a sincronização (os lançamentos não serão restaurados automaticamente).' },
          ]}
          confirmText="Sim, bloquear e excluir lançamentos"
          cancelText="Cancelar"
          type="danger"
        />
      )}

      <div className="space-y-4 sm:space-y-6">
          {/* Header com botão hambúrguer para mobile */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl px-4 sm:px-6 py-4 sm:py-6">
            <div className="flex items-center justify-between mb-4 md:hidden">
              <button
                onClick={() => setSidebarOpen(true)}
                className="p-2 rounded-xl text-white hover:bg-white/20 transition-all duration-200"
              >
                <Menu className="h-6 w-6" />
              </button>
              <h1 className="text-lg font-semibold text-white truncate">Leilões</h1>
              <div className="w-10"></div>
            </div>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4">
              <div className="min-w-0 flex-1">
                <h1 className="text-xl sm:text-3xl font-bold text-white hidden md:block">Leilões</h1>
                <p className="text-blue-100 mt-1 text-sm sm:text-base hidden sm:block">Gerencie todos os seus leilões</p>
              </div>
              {canEdit && (
                <button
                  onClick={() => window.location.href = '/leiloes/novo'}
                  className="w-full sm:w-auto bg-white hover:bg-gray-100 text-blue-600 px-4 sm:px-6 py-3 rounded-xl flex items-center justify-center gap-2 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 font-medium text-sm sm:text-base"
                >
                  <Plus size={18} />
                  Novo Leilão
                </button>
              )}
            </div>
          </div>

        {/* Busca e Filtros */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
          {/* Barra de Busca */}
          <div className="mb-4 sm:mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Buscar por nome do leilão ou cidade..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value)
                  setCurrentPage(1)
                }}
                className="pl-10 pr-4 py-3 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-rematei-blue focus:border-transparent"
              />
            </div>
          </div>
          
          {/* Filtros */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Filter className="w-4 h-4 inline mr-1" />
                Tipo de Leilão
              </label>
              <select
                value={filtros.tipo_leilao}
                onChange={(e) => {
                  setFiltros({...filtros, tipo_leilao: e.target.value})
                  setCurrentPage(1)
                }}
                className="input-rematei"
              >
                <option value="">Todos os tipos</option>
                <option value="gado_corte">Gado de Corte</option>
                <option value="gado_leite">Gado de Leite</option>
                <option value="gado_elite">Gado de Elite</option>
                <option value="cavalo">Cavalo</option>
                <option value="beneficente">Beneficente</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                value={filtros.status}
                onChange={(e) => {
                  setFiltros({...filtros, status: e.target.value})
                  setCurrentPage(1)
                }}
                className="input-rematei"
              >
                <option value="">Todos os status</option>
                <option value="on">Ativo</option>
                <option value="off">Inativo</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Data Início
              </label>
              <input
                type="date"
                value={filtros.data_inicio}
                onChange={(e) => {
                  setFiltros({...filtros, data_inicio: e.target.value})
                  setCurrentPage(1)
                }}
                className="input-rematei"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Data Fim
              </label>
              <input
                type="date"
                value={filtros.data_fim}
                onChange={(e) => {
                  setFiltros({...filtros, data_fim: e.target.value})
                  setCurrentPage(1)
                }}
                className="input-rematei"
              />
            </div>
          </div>
          
          {/* Limpar Filtros */}
          {(searchTerm || filtros.tipo_leilao || filtros.status || filtros.data_inicio || filtros.data_fim) && (
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => {
                  setSearchTerm('')
                  setFiltros({ tipo_leilao: '', status: '', data_inicio: '', data_fim: '' })
                  setCurrentPage(1)
                }}
                className="text-sm text-gray-600 hover:text-gray-800"
              >
                Limpar filtros
              </button>
            </div>
          )}
        </div>

        {/* Resultados e Lista */}
        <div className="space-y-4">
          {/* Header dos Resultados */}
          <div className="flex flex-wrap items-center justify-between gap-1">
            <p className="text-sm text-gray-600">
              {loading ? 'Carregando...' : `${totalItems} leilões encontrados`}
            </p>
            <div className="text-sm text-gray-600">
              Página {currentPage} de {totalPages}
            </div>
          </div>
          
          {/* Lista de Leilões */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            {leiloes.length === 0 && !loading ? (
              <div className="text-center py-12">
                <Gavel className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {searchTerm || filtros.tipo_leilao || filtros.status || filtros.data_inicio || filtros.data_fim
                    ? 'Nenhum leilão encontrado com os filtros aplicados'
                    : 'Nenhum leilão encontrado'
                  }
                </h3>
                <p className="text-gray-500 mb-4">
                  {searchTerm || filtros.tipo_leilao || filtros.status || filtros.data_inicio || filtros.data_fim
                    ? 'Tente ajustar os filtros ou criar um novo leilão'
                    : 'Comece criando seu primeiro leilão'
                  }
                </p>
                {canEdit && (
                  <button
                    onClick={() => window.location.href = '/leiloes/novo'}
                    className="btn-primary"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Novo Leilão
                  </button>
                )}
              </div>
            ) : (
              <>
                {/* Lista em Cards (telas menores) */}
                <div className="2xl:hidden grid grid-cols-1 lg:grid-cols-2 gap-3 p-3 sm:p-4">
                  {leiloes.map((leilao) => (
                    <div key={leilao.id} className="p-4 space-y-3 border border-gray-200 rounded-lg bg-white">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-gray-900 break-words">{leilao.nome}</div>
                          <div className="text-xs text-gray-500">{leilao.codigo_evento || `ID: ${leilao.id}`}</div>
                        </div>
                        <span className={`flex-shrink-0 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          leilao.status === 'on' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {leilao.status === 'on' ? 'Ativo' : 'Inativo'}
                        </span>
                      </div>

                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm text-gray-600">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTipoColor(leilao.tipo_leilao)}`}>
                          {getTipoLabel(leilao.tipo_leilao)}
                        </span>
                        <span className="inline-flex items-center">
                          <Calendar className="w-4 h-4 mr-1 text-gray-400 flex-shrink-0" />
                          {formatDate(leilao.data_leilao, leilao.hora_inicio)}
                        </span>
                        {(leilao.cidade || leilao.uf) && (
                          <span className="inline-flex items-center">
                            <MapPin className="w-4 h-4 mr-1 text-gray-400 flex-shrink-0" />
                            {leilao.cidade}{leilao.cidade && leilao.uf && ', '}{leilao.uf}
                          </span>
                        )}
                        <span className="inline-flex items-center gap-1">
                          <Gavel className="w-4 h-4 text-gray-400" />
                          {leilao.total_lotes ?? 0} lotes
                        </span>
                      </div>

                      {temGestaoFinanceira && leilao.bloquear_sync_gestao && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
                          <BanIcon className="w-3 h-3" />
                          Sem sync
                        </span>
                      )}

                      <div className="flex items-center justify-between gap-2 pt-2 border-t border-gray-100">
                        <div className="flex items-center gap-1">
                          {canEdit && (
                            <button
                              onClick={() => toggleStatus(leilao)}
                              className={`p-2 rounded transition-colors ${
                                leilao.status === 'on' ? 'text-green-600 hover:bg-green-50' : 'text-gray-400 hover:bg-gray-50'
                              }`}
                              title={leilao.status === 'on' ? 'Desativar' : 'Ativar'}
                            >
                              {leilao.status === 'on' ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                            </button>
                          )}
                          {renderCatalogo(leilao)}
                        </div>
                        {renderAcoes(leilao)}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Lista em Tabela (telas grandes) */}
                <div className="hidden 2xl:block overflow-x-auto">
                  <table className="w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Leilão
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Data
                        </th>
                        <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                          Lotes
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                          Local
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                          Catálogo
                        </th>
                        <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Ações
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {leiloes.map((leilao) => (
                        <tr key={leilao.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-3 py-3 min-w-[220px] max-w-[380px]">
                            <div className="text-sm font-medium text-gray-900 break-words">
                              {leilao.nome}
                            </div>
                            <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                              <span>{leilao.codigo_evento || `ID: ${leilao.id}`}</span>
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getTipoColor(leilao.tipo_leilao)}`}>
                                {getTipoLabel(leilao.tipo_leilao)}
                              </span>
                            </div>
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900">
                            <div className="flex items-center">
                              <Calendar className="w-4 h-4 mr-1 text-gray-400 flex-shrink-0" />
                              {formatDate(leilao.data_leilao, leilao.hora_inicio)}
                            </div>
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap text-sm text-center hidden lg:table-cell">
                            <span className="font-medium text-gray-700">
                              {leilao.total_lotes ?? 0}
                            </span>
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-500 hidden lg:table-cell">
                            {(leilao.cidade || leilao.uf) ? (
                              <div className="flex items-center">
                                <MapPin className="w-4 h-4 mr-1 text-gray-400 flex-shrink-0" />
                                {leilao.cidade}{leilao.cidade && leilao.uf && ', '}{leilao.uf}
                              </div>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap">
                            <div className="flex flex-col items-start gap-1">
                              <div className="flex items-center space-x-2">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                leilao.status === 'on'
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-gray-100 text-gray-800'
                              }`}>
                                {leilao.status === 'on' ? 'Ativo' : 'Inativo'}
                              </span>
                              {canEdit && (
                                <button
                                  onClick={() => toggleStatus(leilao)}
                                  className={`p-1 rounded transition-colors ${
                                    leilao.status === 'on'
                                      ? 'text-green-600 hover:bg-green-50'
                                      : 'text-gray-400 hover:bg-gray-50'
                                  }`}
                                  title={leilao.status === 'on' ? 'Desativar' : 'Ativar'}
                                >
                                  {leilao.status === 'on' ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                                </button>
                              )}
                              </div>
                              {temGestaoFinanceira && leilao.bloquear_sync_gestao && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700" title="Sincronização com Gestão Financeira bloqueada">
                                  <BanIcon className="w-3 h-3" />
                                  Sem sync
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap hidden lg:table-cell">
                            {renderCatalogo(leilao)}
                          </td>

                          <td className="px-3 py-3 whitespace-nowrap text-right text-sm font-medium">
                            {renderAcoes(leilao)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {/* Paginação */}
                {totalPages > 1 && (
                  <div className="border-t border-gray-200 px-4 sm:px-6 py-4">
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                      <div className="text-sm text-gray-600 text-center sm:text-left">
                        Mostrando {((currentPage - 1) * itemsPerPage) + 1} a {Math.min(currentPage * itemsPerPage, totalItems)} de {totalItems} resultados
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                          disabled={currentPage === 1}
                          className={`p-2 rounded-lg ${
                            currentPage === 1
                              ? 'text-gray-400 cursor-not-allowed'
                              : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
                          }`}
                        >
                          <ChevronLeft className="w-5 h-5" />
                        </button>
                        
                        {/* Números das páginas */}
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          const pageNum = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i
                          if (pageNum > totalPages) return null
                          
                          return (
                            <button
                              key={pageNum}
                              onClick={() => setCurrentPage(pageNum)}
                              className={`px-3 py-2 rounded-lg text-sm font-medium ${
                                currentPage === pageNum
                                  ? 'bg-rematei-blue text-white'
                                  : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
                              }`}
                            >
                              {pageNum}
                            </button>
                          )
                        })}
                        
                        <button
                          onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                          disabled={currentPage === totalPages}
                          className={`p-2 rounded-lg ${
                            currentPage === totalPages
                              ? 'text-gray-400 cursor-not-allowed'
                              : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
                          }`}
                        >
                          <ChevronRight className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  )
}